import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api, getErrorMessage } from "../../../api/client.ts";
import BackupCodesList from "../../../components/BackupCodesList.tsx";
import TotpInput from "../../../components/TotpInput.tsx";
import { useAuth } from "../../../contexts/AuthContext.tsx";

/** A fresh set of backup codes, confirmed with an authenticator code. Mounted only while open. */
export default function RegenerateBackupCodesDialog({ onClose }: { onClose: () => void }) {
  const { user, refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);

  const regenerate = useMutation({
    mutationFn: (totp: string) => api.regenerateBackupCodes(totp),
    meta: { suppressErrorToast: true },
    onSuccess: async (res) => {
      setCodes(res.backup_codes);
      await refreshUser();
      toast.success("New backup codes generated");
    },
  });

  useEffect(() => {
    if (code.length === 6 && !regenerate.isPending) regenerate.mutate(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (codes) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogTitle>Save your new backup codes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Your old codes no longer work. Each of these signs you in once; they won't be shown again.
          </Typography>
          <BackupCodesList codes={codes} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={regenerate.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate new backup codes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Enter a code from your authenticator app. Your current backup codes stop working.
        </Typography>
        {regenerate.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getErrorMessage(regenerate.error)}
          </Alert>
        )}
        <TotpInput
          value={code}
          onChange={setCode}
          autoFocus
          disabled={regenerate.isPending}
          error={regenerate.isError && code.length === 6}
          username={user?.email}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={regenerate.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => regenerate.mutate(code)}
          loading={regenerate.isPending}
          disabled={code.length < 6}
        >
          Generate codes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
